/**
 * CustomFieldSettings page - manage custom fields for a project
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { projectsApi, type Project } from '@services/api/projects';
import { useAuthStore } from '@stores/authStore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clsx } from 'clsx';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Eye,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CustomField,
  FieldType,
  FieldOption,
  CreateCustomFieldRequest,
  UpdateCustomFieldRequest,
  customFieldsApi,
} from '@services/api/customFields';

const FIELD_TYPE_VALUES: FieldType[] = [
  'text',
  'textarea',
  'number',
  'date',
  'datetime',
  'select',
  'multiselect',
  'checkbox',
  'url',
  'email',
  'user',
];

// Labels resolved through t() so they follow runtime language switches
const getFieldTypes = (t: TFunction): { value: FieldType; label: string; description: string }[] =>
  FIELD_TYPE_VALUES.map((value) => ({
    value,
    label: t(`settings.customFields.fieldTypes.${value}.label`),
    description: t(`settings.customFields.fieldTypes.${value}.description`),
  }));

const getFieldSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t('settings.customFields.validation.nameRequired')).max(100),
    key: z
      .string()
      .min(1, t('settings.customFields.validation.keyRequired'))
      .max(50)
      .regex(/^[a-z][a-z0-9_]*$/, t('settings.customFields.validation.keyPattern')),
    fieldType: z.string() as z.ZodType<FieldType>,
    description: z.string().max(500).optional(),
    isRequired: z.boolean().default(false),
    showOnCard: z.boolean().default(false),
    options: z.array(z.object({
      value: z.string().min(1),
      label: z.string().min(1),
      color: z.string().optional(),
    })).optional(),
  });

type FieldFormData = z.infer<ReturnType<typeof getFieldSchema>>;

export default function CustomFieldSettings() {
  const { t } = useTranslation();
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const organizationId = useAuthStore((state) => state.currentOrgId);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProjects([]);
    setProjectId('');
    setError(null);
    if (!organizationId || routeProjectId) return;
    setLoading(true);
    const load = async () => {
      try {
        const items: Project[] = [];
        for (let page = 1; ; page++) {
          const result = await projectsApi.list(organizationId, { page, pageSize: 100 });
          items.push(...result.items);
          if (cancelled) return;
          if (!result.items.length || items.length >= result.total) break;
        }
        setProjects(items);
      } catch {
        if (!cancelled) setError(t('settings.customFields.failedToLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [organizationId, routeProjectId]);

  const selectedId = routeProjectId || projectId;
  return (
    <div>
      {!routeProjectId && (
        <div className="max-w-4xl mx-auto px-6 pt-6">
          <label htmlFor="custom-fields-project" className="block text-sm font-medium mb-2">
            {t('settings.customFields.projectLabel', 'Project')}
          </label>
          <select id="custom-fields-project" value={projectId}
            onChange={(event) => setProjectId(event.target.value)} disabled={loading || !organizationId}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 dark:border-neutral-600 dark:bg-neutral-800">
            <option value="">{t('settings.customFields.selectProject', 'Select a project')}</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
          {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
          {!loading && !error && !selectedId && <p className="mt-3 text-sm text-neutral-500">
            {t('settings.customFields.projectHint', 'Custom fields belong to a project. Select one to manage its fields.')}
          </p>}
        </div>
      )}
      {selectedId && <ProjectCustomFields key={selectedId} projectId={selectedId} />}
    </div>
  );
}

function ProjectCustomFields({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [deletingField, setDeletingField] = useState<CustomField | null>(null);

  useEffect(() => {
    if (projectId) {
      loadFields();
    }
  }, [projectId]);

  const loadFields = async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await customFieldsApi.list(projectId);
      setFields(data);
    } catch (err: any) {
      setError(err.message || t('settings.customFields.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateCustomFieldRequest | UpdateCustomFieldRequest) => {
    if (!projectId) return;

    try {
      const newField = await customFieldsApi.create(projectId, data as CreateCustomFieldRequest);
      setFields((prev) => [...prev, newField]);
      setIsCreateOpen(false);
    } catch (err: any) {
      throw new Error(err.message || t('settings.customFields.failedToCreate'));
    }
  };

  const handleUpdate = async (fieldId: string, data: UpdateCustomFieldRequest) => {
    if (!projectId) return;

    try {
      const updated = await customFieldsApi.update(projectId, fieldId, data);
      setFields((prev) => prev.map((f) => (f.id === fieldId ? updated : f)));
      setEditingField(null);
    } catch (err: any) {
      throw new Error(err.message || t('settings.customFields.failedToUpdate'));
    }
  };

  const handleDelete = async (fieldId: string) => {
    if (!projectId) return;

    try {
      await customFieldsApi.delete(projectId, fieldId);
      setFields((prev) => prev.filter((f) => f.id !== fieldId));
      setDeletingField(null);
    } catch (err: any) {
      throw new Error(err.message || t('settings.customFields.failedToDelete'));
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    if (!projectId) return;

    const reordered = [...fields];
    const [removed] = reordered.splice(fromIndex, 1);
    if (!removed) return;
    reordered.splice(toIndex, 0, removed);

    // Optimistic update
    setFields(reordered);

    try {
      await customFieldsApi.reorder(
        projectId,
        reordered.map((f) => f.id)
      );
    } catch (err) {
      // Revert on error
      loadFields();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {t('settings.customFields.title')}
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {t('settings.customFields.description')}
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg',
            'bg-primary-600 text-white hover:bg-primary-700',
            'transition-colors'
          )}
        >
          <Plus className="h-4 w-4" />
          {t('settings.customFields.addField')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {fields.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
          <p className="text-neutral-500 dark:text-neutral-400">
            {t('settings.customFields.noFields')}
          </p>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="mt-4 text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            {t('settings.customFields.createFirst')}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <FieldRow
              key={field.id}
              field={field}
              index={index}
              totalFields={fields.length}
              onEdit={() => setEditingField(field)}
              onDelete={() => setDeletingField(field)}
              onMoveUp={() => handleReorder(index, index - 1)}
              onMoveDown={() => handleReorder(index, index + 1)}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <FieldFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
        title={t('settings.customFields.createCustomField')}
      />

      {/* Edit Dialog */}
      {editingField && (
        <FieldFormDialog
          open={!!editingField}
          onClose={() => setEditingField(null)}
          onSubmit={(data) => handleUpdate(editingField.id, data)}
          title={t('settings.customFields.editField')}
          initialData={editingField}
          isEdit
        />
      )}

      {/* Delete Confirmation */}
      {deletingField && (
        <DeleteConfirmDialog
          open={!!deletingField}
          onClose={() => setDeletingField(null)}
          onConfirm={() => handleDelete(deletingField.id)}
          fieldName={deletingField.name}
        />
      )}
    </div>
  );
}

// Field Row Component
interface FieldRowProps {
  field: CustomField;
  index: number;
  totalFields: number;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function FieldRow({
  field,
  index,
  totalFields,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: FieldRowProps) {
  const { t } = useTranslation();
  const typeInfo = getFieldTypes(t).find((ft) => ft.value === field.fieldType);

  return (
    <div
      className={clsx(
        'flex items-center gap-4 p-4 rounded-lg border',
        'bg-white dark:bg-neutral-800',
        'border-neutral-200 dark:border-neutral-700'
      )}
    >
      <div className="flex flex-col gap-1">
        <button
          onClick={onMoveUp}
          disabled={index === 0}
          className={clsx(
            'p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={index === totalFields - 1}
          className={clsx(
            'p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-900 dark:text-white">
            {field.name}
          </span>
          {field.isRequired && (
            <span className="text-xs text-red-500">{t('settings.customFields.required')}</span>
          )}
          {field.showOnCard && (
            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <Eye className="h-3 w-3" />
              {t('settings.customFields.cardLabel')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          <span className="font-mono text-xs">{field.key}</span>
          <span>•</span>
          <span>{typeInfo?.label || field.fieldType}</span>
          {field.description && (
            <>
              <span>•</span>
              <span className="truncate">{field.description}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className={clsx(
            'p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700',
            'text-neutral-600 dark:text-neutral-400'
          )}
          title={t('settings.customFields.editFieldTitle')}
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className={clsx(
            'p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20',
            'text-red-600 dark:text-red-400'
          )}
          title={t('settings.customFields.deleteFieldTitle')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Field Form Dialog
interface FieldFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCustomFieldRequest | UpdateCustomFieldRequest) => Promise<void>;
  title: string;
  initialData?: CustomField;
  isEdit?: boolean;
}

function FieldFormDialog({
  open,
  onClose,
  onSubmit,
  title,
  initialData,
  isEdit = false,
}: FieldFormDialogProps) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<FieldOption[]>(initialData?.options || []);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FieldFormData>({
    resolver: zodResolver(getFieldSchema(t)),
    defaultValues: {
      name: initialData?.name || '',
      key: initialData?.key || '',
      fieldType: initialData?.fieldType || 'text',
      description: initialData?.description || '',
      isRequired: initialData?.isRequired || false,
      showOnCard: initialData?.showOnCard || false,
    },
  });

  const fieldType = watch('fieldType');
  const needsOptions = fieldType === 'select' || fieldType === 'multiselect';

  useEffect(() => {
    if (initialData?.options) {
      setOptions(initialData.options);
    }
  }, [initialData]);

  const handleFormSubmit = async (data: FieldFormData) => {
    setSubmitError(null);
    try {
      // Build submit data, filtering out undefined optional fields for exactOptionalPropertyTypes
      const submitData: CreateCustomFieldRequest | UpdateCustomFieldRequest = {
        name: data.name,
        key: data.key,
        fieldType: data.fieldType,
        isRequired: data.isRequired,
        showOnCard: data.showOnCard,
        ...(data.description ? { description: data.description } : {}),
        ...(needsOptions && options.length > 0 ? { options } : {}),
      };
      await onSubmit(submitData);
      reset();
      setOptions([]);
    } catch (err: any) {
      setSubmitError(err.message);
    }
  };

  const handleClose = () => {
    reset();
    setOptions([]);
    setSubmitError(null);
    onClose();
  };

  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      { value: `option_${prev.length + 1}`, label: `Option ${prev.length + 1}` },
    ]);
  };

  const updateOption = (index: number, field: keyof FieldOption, value: string) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    );
  };

  const removeOption = (index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const baseInputClass = clsx(
    'w-full rounded-lg border px-3 py-2',
    'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
    'border-neutral-300 dark:border-neutral-700',
    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
  );

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-[71] w-full max-w-lg max-h-[85vh] overflow-y-auto',
            '-translate-x-1/2 -translate-y-1/2',
            'rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900'
          )}
        >
          <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
            {title}
          </Dialog.Title>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-4 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('settings.customFields.fieldName')}
              </label>
              <input
                {...register('name')}
                type="text"
                className={clsx(baseInputClass, errors.name && 'border-red-500')}
                placeholder={t('settings.customFields.fieldNamePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Key */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('settings.customFields.fieldKey')}
              </label>
              <input
                {...register('key')}
                type="text"
                disabled={isEdit}
                className={clsx(
                  baseInputClass,
                  errors.key && 'border-red-500',
                  isEdit && 'opacity-50 cursor-not-allowed'
                )}
                placeholder={t('settings.customFields.fieldKeyPlaceholder')}
              />
              {errors.key && (
                <p className="mt-1 text-xs text-red-500">{errors.key.message}</p>
              )}
              {!isEdit && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t('settings.customFields.fieldKeyHint')}
                </p>
              )}
            </div>

            {/* Field Type */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('settings.customFields.fieldType')}
              </label>
              <select
                {...register('fieldType')}
                disabled={isEdit}
                className={clsx(
                  baseInputClass,
                  isEdit && 'opacity-50 cursor-not-allowed'
                )}
              >
                {getFieldTypes(t).map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
              {isEdit && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t('settings.customFields.fieldKeyCannotChange')}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('settings.customFields.fieldDescription')}
              </label>
              <textarea
                {...register('description')}
                rows={2}
                className={baseInputClass}
                placeholder={t('settings.customFields.fieldDescPlaceholder')}
              />
            </div>

            {/* Options for select/multiselect */}
            {needsOptions && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('settings.customFields.options')}
                </label>
                <div className="space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => updateOption(index, 'label', e.target.value)}
                        placeholder={t('settings.customFields.label')}
                        className={clsx(baseInputClass, 'flex-1')}
                      />
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) => updateOption(index, 'value', e.target.value)}
                        placeholder={t('settings.customFields.value')}
                        className={clsx(baseInputClass, 'w-32')}
                      />
                      <input
                        type="color"
                        value={option.color || '#6B7280'}
                        onChange={(e) => updateOption(index, 'color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                        title="Color"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/20"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 text-sm',
                      'text-primary-600 hover:bg-primary-50 rounded',
                      'dark:text-primary-400 dark:hover:bg-primary-900/20'
                    )}
                  >
                    <Plus className="h-4 w-4" />
                    {t('settings.customFields.addOption')}
                  </button>
                </div>
              </div>
            )}

            {/* Toggles */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('isRequired')}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {t('settings.customFields.required')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('showOnCard')}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {t('settings.customFields.showOnCard')}
                </span>
              </label>
            </div>

            {submitError && (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSubmitting ? (
                  t('settings.customFields.saving')
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEdit ? t('common.saveChanges') : t('settings.customFields.createField')}
                  </>
                )}
              </button>
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              className={clsx(
                'absolute right-4 top-4 rounded p-1',
                'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-500',
                'dark:hover:bg-neutral-800'
              )}
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Delete Confirmation Dialog
interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  fieldName: string;
}

function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  fieldName,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-[71] w-full max-w-md',
            '-translate-x-1/2 -translate-y-1/2',
            'rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900'
          )}
        >
          <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
            {t('settings.customFields.deleteField')}
          </Dialog.Title>

          <p className="mt-2 text-neutral-600 dark:text-neutral-400">
            {t('settings.customFields.deleteFieldDesc', { name: fieldName })}
          </p>

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className={clsx(
                'rounded-lg px-4 py-2 text-sm font-medium',
                'text-neutral-700 hover:bg-neutral-100',
                'dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isDeleting}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
                'bg-red-600 text-white hover:bg-red-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isDeleting ? (
                t('common.deleting')
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  {t('settings.customFields.deleteFieldButton')}
                </>
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export { CustomFieldSettings };
